import React from "react";

import type { SvgIconComponent } from "@mui/icons-material";
import { Box, Button, Typography, useTheme, alpha } from "@mui/material";

/** Icon type: MUI SvgIconComponent or custom Rusha icon component */
type EmptyStateIcon =
  SvgIconComponent | React.FC<{ size?: number; sx?: object }>;

interface EmptyStateProps {
  icon: EmptyStateIcon;
  title: string;
  subtitle?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

/** Check if the icon is a custom Rusha icon (has size prop) vs MUI SvgIconComponent */
function isCustomIcon(
  icon: EmptyStateIcon,
): icon is React.FC<{ size?: number; sx?: object }> {
  return (
    typeof icon === "function" && !("$$typeof" in icon) && !("render" in icon)
  );
}

export function EmptyState({
  icon: Icon,
  title,
  subtitle,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
}: EmptyStateProps) {
  const theme = useTheme();

  const renderIcon = () => {
    if (isCustomIcon(Icon)) {
      return <Icon size={32} sx={{ color: "text.disabled" }} />;
    }
    const MuiIcon = Icon as SvgIconComponent;
    return <MuiIcon sx={{ fontSize: 32, color: "text.disabled" }} />;
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        px: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 64,
          height: 64,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          mb: 1,
        }}
      >
        {renderIcon()}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 400 }}
        >
          {subtitle}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Button
          variant="contained"
          size="small"
          onClick={onAction}
          sx={{
            mt: 1,
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 600,
            transition: "all 150ms ease",
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          {actionLabel}
        </Button>
      )}
      {secondaryActionLabel && onSecondaryAction && (
        <Button
          variant="text"
          size="small"
          onClick={onSecondaryAction}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
          }}
        >
          {secondaryActionLabel}
        </Button>
      )}
    </Box>
  );
}
